import { useSignedInState, useSignedInUserState } from "../hooks/useAuthState";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import { Theme, useTheme } from "@mui/material/styles";
import { Box, Button, ListItemIcon, useMediaQuery, Typography, IconButton, SwipeableDrawer } from "@mui/material";
import { AuthButton } from "../shared/authButton";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapDialog, { Coordinates } from "../listingSearchPage/dialog/mapDialog";
import DashboardIcon from '@mui/icons-material/Dashboard';

interface SideDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

interface MenuItem {
    name: string;
    icon: ReactNode;
    action: () => void;
}

const SideDrawer: React.FC<SideDrawerProps> = ({ isOpen, onClose }) => {
    let user = useSignedInUserState()
    const userProfile = user.profile;
    const signedIn = useSignedInState();
    const theme: Theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const [openModal, setOpenModal] = useState(false);

    const baseItems = useMemo<MenuItem[]>(() => {
        const items = [
          { name: "Browse Deals", icon: <SearchIcon />, action: () => navigate("/search") },
        ];
      
        if (signedIn && ["staff", "owner", "user", "admin"].includes(userProfile?.profileType || "")) {
          items.push({ name: "Order History", icon: <HistoryIcon />, action: () => navigate("/order/history") });
        }
      
        return items;
      }, [signedIn, userProfile, navigate]);
      
      const manageItems = useMemo<MenuItem[]>(() => {
        const items: MenuItem[] = [];
      
        if (signedIn && ["staff", "owner", "user", "admin"].includes(userProfile?.profileType || "")) {
          items.push(
            { name: "Profile", icon: <AccountCircleIcon />, action: () => navigate("/profile") },
            { name: "Update Location", icon: <LocationOnIcon />, action: () => setOpenModal(true) }
          );
        }
      
        if (signedIn && ["staff", "owner", "admin"].includes(userProfile?.profileType || "")) {
          items.push(
            { name: "Dashboard", icon: <DashboardIcon  />, action: () => navigate("/dashboard") },
          );
        }
      
        if (signedIn && ["admin"].includes(userProfile?.profileType || "")) {
          items.push(
            { name: "Admin", icon: <AdminPanelSettingsIcon  />, action: () => navigate("/adminPortal") },
          );
        }
      
        return items;
      }, [signedIn, userProfile, navigate]);

      const notificationItems = useMemo<MenuItem[]>(() => {
        const items: MenuItem[] = [];
      
        if (signedIn && ["staff", "owner", "user", "admin"].includes(userProfile?.profileType || "")) {
          items.push(
            { name: "Messages", icon: <ChatBubbleOutlineIcon />, action: () => navigate("/messages") }
          );
        }
      
        return items;
      }, [signedIn, userProfile, navigate]);
      
    // Close the modal
    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const onConfirm = async (coords: Coordinates) => {
        setOpenModal(false);
    };

    return (
        <SwipeableDrawer
            anchor="left"
            open={isOpen}
            onOpen={() => { }}
            disableBackdropTransition={!isMobile}
            disableDiscovery={!isMobile}
            onClose={onClose}
            variant="temporary"
            PaperProps={{
                sx: {
                    width: isMobile ? "100%" : 300,
                },
            }}
        >

            <Box
                sx={{
                    backgroundColor: "#F5F9E9",
                    padding: 2,
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
                className="pat"
            >
                <MapDialog open={openModal} onClose={handleCloseModal} onConfirm={onConfirm} user={user} />
                {/* Close/Menu Icon on the left */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        left: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                    }}
                >
                    <MenuIcon />
                </IconButton>

                {/* Centered Logo */}
                <img
                    src="/src_assets/header_logo.svg"
                    alt="Eco Eats"
                    style={{ height: "50px" }}
                />
            </Box>


            <List>
                {/* Shop Section */}
                <Typography variant="subtitle2" sx={{ padding: 2 }}>Shop</Typography>
                {baseItems.map((item, index) => (
                    <ListItem sx={{

                        backgroundColor: "transparent !important", // Remove background color
                        border: "none !important", // Remove border
                        "&.Mui-selected": {
                            backgroundColor: "transparent !important",
                        },
                        "&:hover": {
                            backgroundColor: "transparent!",
                        },
                        "&:focus-visible": {
                            outline: "none",
                        },
                    }} color="transparent" component={"button"} key={index} onClick={() => { item.action(); onClose(); }}>
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.name} />
                    </ListItem>
                ))}
                <Divider />

                {/* Manage Section */}
                {manageItems.length > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={{ padding: 2 }}>Manage</Typography>
                        {manageItems.map((item, index) => (
                            <ListItem sx={{

                                backgroundColor: "transparent !important", // Remove background color
                                border: "none !important", // Remove border
                                "&.Mui-selected": {
                                    backgroundColor: "transparent !important",
                                },
                                "&:hover": {
                                    backgroundColor: "transparent!",
                                },
                                "&:focus-visible": {
                                    outline: "none",
                                },
                            }} component={"button"} key={index} onClick={() => { item.action(); onClose(); }}>
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.name} />
                            </ListItem>
                        ))}
                        <Divider />
                    </>
                )}

                {/* Notifications Section */}
                {notificationItems.length > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={{ padding: 2 }}>Notifications</Typography>
                        {notificationItems.map((item, index) => (
                            <ListItem
                                sx={{
                                    backgroundColor: "transparent !important",
                                    border: "none !important",
                                    "&.Mui-selected": {
                                        backgroundColor: "transparent !important",
                                    },
                                    "&:hover": {
                                        backgroundColor: "transparent !important",
                                    },
                                    "&:focus-visible": {
                                        outline: "none",
                                    },
                                }}
                                component="button"
                                key={index}
                                onClick={() => {
                                    item.action();
                                    onClose();
                                }}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.name} />
                            </ListItem>
                        ))}
                    </>
                )}
                <Divider />
            </List>

            {/* Sign-Out Button at the Bottom */}
            <Box sx={{ marginTop: "auto", padding: 4 }}>
                <AuthButton fullWidth />
            </Box>
        </SwipeableDrawer>
    );
};

export default SideDrawer;
